<template>
    <b-modal static v-model="verifyPhone" :id="modalId" @hidden="$emit('closed')" dialog-class="login-dialogClass" hide-footer size="md" title="Link Phone" centered>
        <div v-if="errorMessage" class="alert alert-warning" role="alert">{{ errorMessage }}</div>
        <b-button v-if="showDone && refreshFailed" @click="refreshAfterVerification" :disabled="saving" class="mb-3">Refresh account status</b-button>
        <div v-if="showPhoneInput && !showDone" class="text-center vstack gap-3">
            <p>
                <font-awesome-icon icon="fa-solid fa-mobile-screen" class="icon-size" />
            </p>
            <p>
                Import Previous RSVPs<br />
                Get invites from your contacts
            </p>

            <MazPhoneNumberInput :key="generation" :disabled="saving" class="w-100" size="sm" show-code-on-list @update="handlePhoneData" />

            <b-button @click="getCode" pill :disabled="!phoneValid || saving" variant="primary" class="w-100">Get Code</b-button>
            <b-button class="w-100" :disabled="saving" @click="showPhoneInput = false">I already have a code</b-button>
        </div>
        <div v-if="!showPhoneInput && !showDone" class="text-center vstack gap-3">
            <p>Enter code</p>

            <b-form-input class="text-center" aria-label="Verification code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" :disabled="saving" v-model="code"></b-form-input>
            <b-button @click="verifyCode" pill :disabled="!codeValid || saving" variant="primary" class="w-100">Confirm</b-button>
            <b-button :disabled="saving" @click="showPhoneInput = true" class="w-100">Cancel</b-button>
        </div>
        <div v-if="showDone" class="text-center vstack gap-3">
            <p>
                <font-awesome-icon icon="fa-solid fa-circle-check" class="text-success icon-size" />
            </p>
            <h4>All Set!</h4>
        </div>
    </b-modal>
</template>


<script lang="ts">
import { PropType, defineComponent, inject, ref, useId } from "vue";
import MazPhoneNumberInput from "maz-ui/components/MazPhoneNumberInput";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useToast } from "vue-toastification";
import Api from "@/Api";
import AuthManager from "@/AuthManager";
import EventViewModel from "@/EventViewModel";
import { smsErrorDetails } from "@/util/SmsErrors";

export default defineComponent({
    components: {
        MazPhoneNumberInput,
        FontAwesomeIcon,
    },
    emits: ["verified", "closed"],
    props: {
        eventViewModel: {
            type: Object as PropType<EventViewModel | null>,
        },
    },
    data() {
        return {
            saving: false,
            generation: 0,
            errorMessage: "",
            refreshFailed: false,
            phone: "",
            phoneValid: false,
            showPhoneInput: true,
            showDone: false,
        };
    },
    setup() {
        const verifyPhone = ref(false);
        const code = ref("");
        const toast = useToast();
        const api = inject<Api>("api")!;
        const authManager = inject<AuthManager>("authManager")!;

        return {
            modalId: useId(),
            verifyPhone,
            code,
            toast,
            api,
            authManager,
        };
    },
    computed: {
        codeValid() {
            return this.code.length == 6 && /^[0-9]+$/.test(this.code);
        },
    },
    watch: {
        "authManager.accountRevision"() { this.hideModal(); },
        verifyPhone(value: boolean) {
            if (!value) { this.generation++; this.saving = false; }
        },
    },
    beforeUnmount() { this.generation++; },
    methods: {
        showModal() {
            if (this.verifyPhone) return;
            this.generation++;
            this.saving = false;
            this.phone = "";
            this.phoneValid = false;
            this.code = "";
            this.showPhoneInput = true;
            this.showDone = false;
            this.errorMessage = "";
            this.refreshFailed = false;
            this.verifyPhone = true;
        },
        hideModal() {
            this.verifyPhone = false;
        },
        handlePhoneData(results: any) {
            this.phoneValid = results.isValid;
            this.phone = results.e164;
        },
        async getCode() {
            if (this.saving || !this.phoneValid) return;
            const generation = this.generation;
            const revision = this.authManager.accountRevision;
            this.saving = true;
            this.errorMessage = "";
            try {
                await this.api.verifyPhone({ phone: this.phone });
                if (generation === this.generation && revision === this.authManager.accountRevision) this.showPhoneInput = false;
            } catch (error) {
                if (generation === this.generation && revision === this.authManager.accountRevision) {
                    const details = smsErrorDetails(error);
                    this.errorMessage = details.message;
                    if (details.uncertain) this.errorMessage += " If a code arrives, choose ‘I already have a code’.";
                }
            } finally {
                if (generation === this.generation) this.saving = false;
            }
        },
        async verifyCode() {
            if (this.saving || !this.codeValid) return;
            const generation = this.generation;
            const revision = this.authManager.accountRevision;
            this.saving = true;
            this.errorMessage = "";
            try {
                await this.api.verifyPhone({ code: this.code });
            } catch (error) {
                if (generation === this.generation && revision === this.authManager.accountRevision) this.errorMessage = smsErrorDetails(error).message;
                if (generation === this.generation) this.saving = false;
                return;
            }
            if (generation !== this.generation || revision !== this.authManager.accountRevision) return;
            this.showDone = true;
            this.showPhoneInput = false;
            await this.refreshAfterVerification();
        },
        async refreshAfterVerification() {
            const generation = this.generation;
            const revision = this.authManager.accountRevision;
            this.saving = true;
            this.refreshFailed = false;
            try {
                const refreshed = await this.authManager.refreshMetadata();
                if (generation !== this.generation || revision !== this.authManager.accountRevision) return;
                await this.eventViewModel?.loadEvent();
                if (!refreshed || this.eventViewModel?.hasError) throw new Error("Refresh failed");
                if (generation !== this.generation || revision !== this.authManager.accountRevision) return;
                this.errorMessage = "";
                this.$emit("verified");
            } catch {
                if (generation !== this.generation || revision !== this.authManager.accountRevision) return;
                this.refreshFailed = true;
                this.errorMessage = "Your phone was verified, but the latest account or event status could not be loaded. Refresh the status before trying to send again.";
            } finally {
                if (generation === this.generation) this.saving = false;
            }
        },
    },
});
</script>

<style scoped>
.icon-size {
    width: 80px;
    height: 80px;
}
</style>